import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  progressFillRecipe,
  progressTrackRecipe,
  type ProgressTrackRecipeProps,
} from '../recipes/progress.recipe';

export interface ProgressBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    ProgressTrackRecipeProps {
  /** How far along, 0–100. Values outside that are clamped rather than drawn. */
  value: number;
  /**
   * What the bar is measuring, for screen readers — "Upload progress".
   * Omit only when a visible label already names it and is associated by
   * `aria-labelledby`.
   */
  label?: string;
}

/**
 * A determinate progress bar.
 *
 *   <ProgressBar value={62} label="Upload progress" />
 *
 * Announces itself as a progressbar with its value, which none of the five
 * hand-built versions did — they were two nested divs and a percentage, so a
 * screen reader heard nothing at all.
 */
export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  function ProgressBar({ value, label, size, className, ...props }, ref) {
    const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        className={cn(progressTrackRecipe({ size }), className)}
        {...props}
      >
        <div className={progressFillRecipe()} style={{ width: `${pct}%` }} />
      </div>
    );
  }
);
